/**
 * AuthZEN Resource Builder
 * 
 * Creates properly formatted resource objects per OpenID AuthZEN 1.0 Section 5.2
 * 
 * Spec Requirements:
 * - Resource MUST have 'type' field (string)
 * - Resource MUST have 'id' field (string)
 * - Resource MAY have 'properties' field (object)
 * 
 * Handles both specific resources and type-level permissions without placeholder IDs
 */

export interface AuthZENResource {
	type: string;
	id: string;
	properties?: Record<string, any>;
}

export class ResourceBuilder {
	/**
	 * Build AuthZEN-compliant resource object
	 * 
	 * Handles three scenarios:
	 * 1. Specific resource: { type: 'user', id: 'user-123' }
	 * 2. Type-level check: { type: 'admin-panel', id: '*' }
	 * 3. Feature check: { type: 'feature:export', id: '*' }
	 * 
	 * @param {string} type - Resource type (required)
	 * @param {string} id - Resource ID (optional, defaults to '*' for type-level)
	 * @param {object} properties - Additional properties
	 * @returns {AuthZENResource} Spec-compliant resource object
	 * 
	 * @example
	 * ```typescript
	 * // Specific resource
	 * ResourceBuilder.build('user', 'user-123')
	 * // { type: 'user', id: 'user-123' }
	 * 
	 * // Type-level permission (no specific resource)
	 * ResourceBuilder.build('admin-panel')
	 * // { type: 'admin-panel', id: '*' }
	 * 
	 * // With properties
	 * ResourceBuilder.build('document', 'doc-456', { classification: 'secret' })
	 * // { type: 'document', id: 'doc-456', properties: { classification: 'secret' } }
	 * ```
	 */
	static build(
		type: string,
		id?: string,
		properties?: Record<string, any>
	): AuthZENResource {
		// Validate type is provided
		if (!type || typeof type !== 'string') {
			throw new Error('Resource type is required and must be string');
		}
		
		// Build resource with semantic ID
		const resource: AuthZENResource = {
			type,
			id: id || '*'  // Use wildcard for type-level permissions (not placeholder)
		};
		
		// Add properties if provided and non-empty
		if (properties && Object.keys(properties).length > 0) {
			resource.properties = properties;
		}
		
		return resource;
	}
	
	/**
	 * Build resource for page-level authorization
	 * 
	 * Pages don't have specific IDs, so we use the page route as the resource type
	 * and wildcard for ID.
	 * 
	 * @param {string} pageRoute - Page route (e.g., '/admin', '/settings')
	 * @param {object} properties - Additional properties
	 * @returns {AuthZENResource} Page resource
	 * 
	 * @example
	 * ```typescript
	 * ResourceBuilder.forPage('/admin/users')
	 * // { type: 'page:/admin/users', id: '*' }
	 * ```
	 */
	static forPage(
		pageRoute: string,
		properties?: Record<string, any>
	): AuthZENResource {
		// Normalize route (remove leading slash for consistency)
		const normalizedRoute = pageRoute.startsWith('/')
			? pageRoute.substring(1)
			: pageRoute;
		
		return this.build(`page:${normalizedRoute}`, '*', properties);
	}
	
	/**
	 * Build resource for feature-level authorization
	 * 
	 * Features are capabilities or operations that don't map to specific resources.
	 * 
	 * @example
	 * ```typescript
	 * ResourceBuilder.forFeature('export-data')
	 * // { type: 'feature:export-data', id: '*' }
	 * 
	 * ResourceBuilder.forFeature('bulk-delete', { operation_type: 'dangerous' })
	 * // { type: 'feature:bulk-delete', id: '*', properties: { operation_type: 'dangerous' } }
	 * ```
	 */
	static forFeature(
		featureName: string,
		properties?: Record<string, any>
	): AuthZENResource {
		return this.build(`feature:${featureName}`, '*', properties);
	}
	
	/**
	 * Build resource for component-level authorization
	 * 
	 * UI components can be protected based on permissions
	 * 
	 * @example
	 * ```typescript
	 * ResourceBuilder.forComponent('admin-panel')
	 * // { type: 'component:admin-panel', id: '*' }
	 * ```
	 */
	static forComponent(
		componentName: string,
		properties?: Record<string, any>
	): AuthZENResource {
		return this.build(`component:${componentName}`, '*', properties);
	}
	
	/**
	 * Build resource for action-level authorization
	 * 
	 * Some actions don't have a specific resource they operate on
	 * 
	 * @example
	 * ```typescript
	 * ResourceBuilder.forAction('system:shutdown')
	 * // { type: 'action:system:shutdown', id: '*' }
	 * ```
	 */
	static forAction(
		actionName: string,
		properties?: Record<string, any>
	): AuthZENResource {
		return this.build(`action:${actionName}`, '*', properties);
	}
	
	/**
	 * Validate resource object meets spec requirements
	 * 
	 * @throws {Error} If resource is invalid
	 */
	static validate(resource: any): void {
		if (!resource || typeof resource !== 'object') {
			throw new Error('Resource must be an object');
		}
		
		if (!resource.type || typeof resource.type !== 'string') {
			throw new Error('Resource.type is required and must be string');
		}
		
		if (!resource.id || typeof resource.id !== 'string') {
			throw new Error('Resource.id is required and must be string');
		}
		
		if ('properties' in resource && resource.properties !== null && resource.properties !== undefined) {
			if (typeof resource.properties !== 'object') {
				throw new Error('Resource.properties must be object if present');
			}
		}
	}
}

/**
 * Semantic resource type helpers for common scenarios
 */
export const ResourceTypes = {
	// Page-level
	page: (route: string) => ResourceBuilder.forPage(route),
	
	// Feature-level
	feature: (name: string) => ResourceBuilder.forFeature(name),
	
	// Component-level
	component: (name: string) => ResourceBuilder.forComponent(name),
	
	// Action-level
	action: (name: string) => ResourceBuilder.forAction(name),
	
	// Generic builder
	build: (type: string, id?: string, properties?: any) =>
		ResourceBuilder.build(type, id, properties)
};

